const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);

export const formatLakh = (n) => {
  const num = Number(n) || 0;
  if (num >= 100000) return `${(num / 100000).toFixed(2).replace(/\.?0+$/, "")}L`;
  if (num >= 1000) return `${(num / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return fmt(num);
};

export const ragLabel = (status) => {
  if (status === "green") return "Excellent";
  if (status === "amber") return "At Risk";
  return "Critical";
};

export const ragPillClass = (status) => {
  if (status === "green") return "good";
  if (status === "amber") return "amber";
  return "critical";
};

export const healthNarrative = (block, metrics) => {
  const shi = block?.shi_score ?? 0;
  if (shi >= 85) {
    return {
      title: "Strong Block Health with Targeted Risk Pockets",
      body: `${block?.block_name ?? "This block"} has strong student identity and infrastructure indicators. The immediate focus should stay on zero-teacher schools and dropout-risk schools.`,
    };
  }
  if (shi >= 70) {
    return {
      title: "Stable Block Health with Selective Follow-Up Needed",
      body: "Core indicators are holding, but certification, identity completion and staffing gaps need block-level monitoring.",
    };
  }
  return {
    title: "Block Health Needs Urgent Intervention",
    body: `Identity, infrastructure or operational signals are weak across ${metrics?.criticalSchools ?? 0} critical schools requiring immediate review.`,
  };
};

export const buildBlockMetrics = (schools = []) => {
  const criticalSchools = schools.filter((s) => s.rag_status === "red").length;
  const zeroTeacherSchools = schools.filter((s) => (s.total_teachers ?? 0) === 0).length;
  const lowAadhaarSchools = schools.filter((s) => (s.aadhaar_percentage ?? 0) < 70).length;
  const activeSchools = schools.filter((s) => (s.total_students ?? 0) > 0).length;
  return { criticalSchools, zeroTeacherSchools, lowAadhaarSchools, activeSchools };
};

export const buildInsightCards = ({ block, infra, operational, teachers, metrics }) => [
  {
    label: "School Performance",
    title: `${fmt(block?.total_schools ?? 0)} Schools Monitored`,
    body: `${fmt(teachers?.teachers_cy ?? 0)} teachers and ${fmt(block?.total_students ?? 0)} students are covered in the broader block performance scope.`,
  },
  {
    label: "Infrastructure",
    title: `Classrooms ${(infra?.classroom_health_index ?? 0).toFixed(1)}%`,
    body: "Classroom and toilet indicators are on par with block expectations and not the primary constraint.",
  },
  {
    label: "Student Data",
    title: `APAAR ${(block?.apaar_percentage ?? 0).toFixed(1)}%`,
    body: "Generation rate is healthy but still below Aadhaar coverage, creating an identity-completion gap.",
  },
  {
    label: "Critical Concern",
    title: `Dropout ${(operational?.dropout_rate ?? 0).toFixed(2)}%`,
    body: "Requires immediate school-level review and targeted outreach for vulnerable student groups.",
  },
];

export const buildSignalCards = (metrics) => [
  {
    variant: "red",
    title: "Zero-Teacher Risk Pockets",
    body:
      metrics.zeroTeacherSchools > 0
        ? `${metrics.zeroTeacherSchools} schools show 0 teachers in the current table and should be validated against staffing records.`
        : "No zero-teacher schools detected in the current school scope.",
  },
  {
    variant: "orange",
    title: "Dropout Intervention Needed",
    body: "Set up a 30-day campaign for schools where dropout risk combines with low identity completion.",
  },
  {
    variant: "green",
    title: "Infrastructure Strength",
    body: "Use strong infrastructure baseline as a foundation for learning and enrolment-retention programs.",
  },
];

export const buildPerformanceMatrix = ({ block, infra, operational }) => [
  { label: "Aadhaar Compliance", value: block?.aadhaar_percentage ?? 0, fill: "" },
  { label: "Classroom Health", value: infra?.classroom_health_index ?? 0, fill: "green" },
  { label: "APAAR Generation", value: block?.apaar_percentage ?? 0, fill: "" },
  { label: "OSH Score", value: block?.shi_score ?? 0, fill: "green" },
  { label: "Dropout Risk", value: operational?.dropout_rate ?? 0, fill: "red", barScale: true },
];

export const buildActionQueue = (metrics) => [
  {
    title: "Validate Zero-Teacher Schools",
    body: "Cross-check teacher allocation and mark schools requiring immediate deployment.",
    priority: "Critical",
    priorityClass: "red",
  },
  {
    title: "Close APAAR Identity Gap",
    body: "Focus on schools where Aadhaar is strong but APAAR completion is lagging.",
    priority: "High",
    priorityClass: "",
  },
  {
    title: "Start Dropout Outreach Sprint",
    body: "Review high-risk schools and assign block-level follow-up owners.",
    priority: "Planned",
    priorityClass: "green",
  },
].filter((item, idx) => (idx === 0 ? metrics.zeroTeacherSchools > 0 : true));

export const schoolStatusMeta = (rag) => {
  if (rag === "red") return { label: "Critical", className: "critical" };
  if (rag === "green") return { label: "Healthy", className: "ok" };
  return { label: "Watch", className: "watch" };
};

export const filterSchools = (schools, { query, filter, category }) => {
  let rows = [...schools];
  const q = (query || "").trim().toLowerCase();
  if (q) {
    rows = rows.filter(
      (s) =>
        String(s.school_name || "").toLowerCase().includes(q) ||
        String(s.udise_code || "").includes(q),
    );
  }
  if (filter === "critical") rows = rows.filter((s) => s.rag_status === "red");
  if (filter === "zero-teacher") rows = rows.filter((s) => (s.total_teachers ?? 0) === 0);
  if (filter === "low-aadhaar") rows = rows.filter((s) => (s.aadhaar_percentage ?? 0) < 70);
  if (category && category !== "all") {
    rows = rows.filter((s) => (s.school_category || s.school_management || "Uncategorized") === category);
  }
  rows.sort((a, b) => (a.shi_score ?? 0) - (b.shi_score ?? 0));
  return rows;
};

export const BLOCK_KPI_MODAL_CONFIG = {
  total_schools: {
    id: "total_schools",
    title: "Total Schools — Block Scope",
    description: "All schools monitored in this block.",
    filterSchools: (schools) =>
      [...schools].sort((a, b) => String(a.school_name || "").localeCompare(String(b.school_name || ""))),
  },
  total_students: {
    id: "total_students",
    title: "Total Students — Enrolment by School",
    description: "Schools with enrolment, ranked highest to lowest.",
    filterSchools: (schools) =>
      [...schools]
        .filter((s) => (s.total_students ?? 0) > 0)
        .sort((a, b) => (b.total_students ?? 0) - (a.total_students ?? 0)),
  },
  aadhaar_compliance: {
    id: "aadhaar_compliance",
    title: "Aadhaar Compliance — School Drill-Down",
    description: "Schools sorted by Aadhaar coverage (lowest first) to surface identity gaps.",
    filterSchools: (schools) =>
      [...schools].sort((a, b) => (a.aadhaar_percentage ?? 0) - (b.aadhaar_percentage ?? 0)),
  },
  critical_schools: {
    id: "critical_schools",
    title: "Critical Schools — Weekly Follow-Up",
    description: "Schools with critical OSH status requiring immediate block-level review.",
    filterSchools: (schools) =>
      [...schools]
        .filter((s) => s.rag_status === "red")
        .sort((a, b) => (a.shi_score ?? 0) - (b.shi_score ?? 0)),
  },
};

export const getBlockKpiSchools = (metric, schools = []) => {
  const config = BLOCK_KPI_MODAL_CONFIG[metric];
  if (!config) return [];
  return config.filterSchools(schools);
};

export const exportSchoolsCsv = (rows) => {
  const headers = ["School Name", "UDISE", "Category", "Students", "Teachers", "PTR", "Aadhaar %", "OSH Score", "Status"];
  const lines = rows.map((s) => {
    const status = schoolStatusMeta(s.rag_status).label;
    const cat = s.school_category || s.school_management || "";
    return [
      `"${String(s.school_name || "").replace(/"/g, '""')}"`,
      s.udise_code,
      `"${cat.replace(/"/g, '""')}"`,
      s.total_students ?? 0,
      s.total_teachers ?? 0,
      s.ptr ?? 0,
      s.aadhaar_percentage ?? 0,
      s.shi_score ?? 0,
      status,
    ].join(",");
  });
  const blob = new Blob([[headers.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "block_schools.csv";
  a.click();
  URL.revokeObjectURL(a.href);
};
